/**
 * overrides.css: the brand kit on a dark Ink bench. Lighter ink panels with
 * hairline borders, one Stud Orange accent, a blueprint grid that shows as a
 * faint band at the top and as a lens around the cursor.
 *
 * Motion runs on a handful of tokens (--bb-ease*, --bb-dur-*): presses 90 ms,
 * fades 160 ms, moves 240 ms, entrances 320 ms, one 1.6 s loop for "working".
 * Under prefers-reduced-motion nothing moves; colour and opacity fades stay at
 * 120 ms. Decorative hover rules only apply to fine pointers; touch gets press
 * states instead. Desktop-first; the narrow-screen rules live at the end and
 * keep touch targets at 44 px.
 *
 * Orange is reserved for the logo studs, the model, the primary button,
 * selected states, focus rings and links (as the lighter --cfg-x-accent-text
 * when it is text). Stud Yellow only marks print-check warnings. Everything
 * else is ink and Paper.
 */
export const css = /* css */ `
:root {
  color-scheme: dark;
  /* motion */
  --bb-ease: cubic-bezier(0.2, 0.8, 0.2, 1);
  --bb-ease-inout: cubic-bezier(0.4, 0, 0.2, 1);
  --bb-ease-exit: cubic-bezier(0.4, 0, 1, 1);
  --bb-dur-press: 90ms;
  --bb-dur-fade: 160ms;
  --bb-dur-move: 240ms;
  --bb-dur-enter: 320ms;
  --bb-stagger: 30ms;
  --bb-delay-busy: 250ms;
  --bb-dur-loop: 1600ms;
  /* type scale */
  --bb-fs-11: 11px;
  --bb-fs-12: 12px;
  --bb-fs-13: 13px;
  --bb-fs-14: 14px;
  --bb-fs-16: 16px;
  --bb-fs-18: 18px;
  --bb-fs-24: 24px;
  --bb-fs-28: 28px;
  --bb-fs-40: 40px;
  /* surfaces and lines */
  --bb-panel: #1A2434;
  --bb-field: #111925;
  --bb-input-border: #6B768A;
  --bb-input-border-hover: #8590A3;
  --bb-track: #3A4458;
  --bb-warn: var(--cfg-x-warn, #FFB81C);
  --bb-accent-text: var(--cfg-x-accent-text, #FF7A47);
  --bb-disabled-text: rgba(247, 242, 232, 0.38);
  --bb-header-h: 64px;
  --bb-max: 1360px;
  --bb-thumb-filter: drop-shadow(0 10px 14px rgba(5,9,16,0.5));
  scrollbar-color: var(--cfg-color-border-strong) transparent;
  -webkit-tap-highlight-color: transparent;
}
::selection { background: rgba(255,90,31,0.4); color: var(--cfg-color-text); }

html { background: var(--cfg-color-bg); }
body {
  background:
    radial-gradient(1100px 420px at 50% -220px, rgba(255,90,31,0.07), transparent 70%),
    var(--cfg-color-bg);
  color: var(--cfg-color-text);
  font-family: var(--cfg-font-body);
  font-size: var(--cfg-text-base);
  line-height: 1.55;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
.app-shell { position: relative; z-index: 1; overflow-x: clip; }

/* ------------------------------ the bench grid ----------------------------- */
/* Fixed layer behind the page. The top band is always on; the lens is a 440 px
   window moved with transform, its inner grid counter-moved so the dots stay put. */
.bb-bench { position: fixed; inset: 0; z-index: -1; pointer-events: none; overflow: hidden; contain: strict; }
.bb-bench::before, .bb-bench-grid {
  background-image:
    radial-gradient(circle, var(--cfg-x-grid) 1px, transparent 1.4px),
    linear-gradient(rgba(221,226,234,0.035) 1px, transparent 1px),
    linear-gradient(90deg, rgba(221,226,234,0.035) 1px, transparent 1px);
  background-size: 20px 20px, 100px 100px, 100px 100px;
  background-position: -10px -10px, -1px -1px, -1px -1px;
}
.bb-bench::before {
  content: ""; position: absolute; inset: 0;
  -webkit-mask-image: linear-gradient(180deg, #000, transparent 420px);
  mask-image: linear-gradient(180deg, #000, transparent 420px);
  opacity: 0.35;
}
/* The lens never passes under the frosted header, so the header's backdrop blur stays static. */
.bb-bench-clip { position: absolute; inset: 0; clip-path: inset(var(--bb-header-h) 0 0 0); }
.bb-bench-lens {
  position: absolute; left: 0; top: 0; width: 440px; height: 440px; overflow: hidden;
  -webkit-mask-image: radial-gradient(closest-side, #000 0%, rgba(0,0,0,0.35) 45%, transparent 100%);
  mask-image: radial-gradient(closest-side, #000 0%, rgba(0,0,0,0.35) 45%, transparent 100%);
  transform: translate3d(-9999px, -9999px, 0);
  opacity: 0; transition: opacity var(--bb-dur-move) var(--bb-ease);
  will-change: transform, opacity;
}
.bb-bench.is-live .bb-bench-lens { opacity: 0.9; }
.bb-bench-grid { position: absolute; left: 0; top: 0; width: 100vw; height: 100vh; will-change: transform; }

/* ---------------------------------- base ---------------------------------- */
h1, h2, h3, .bb-display { font-family: var(--cfg-font-display); font-weight: 600; letter-spacing: -0.02em; }
a, button.link { color: var(--bb-accent-text); text-underline-offset: 3px; }
a:focus-visible, button:focus-visible, [tabindex]:focus-visible { outline: 2px solid var(--cfg-color-accent); outline-offset: 2px; border-radius: 6px; }
main:focus { outline: none; }
button.link { text-underline-offset: 3px; }
.muted { color: var(--cfg-color-text-muted); }
.small { font-size: var(--bb-fs-12); }
code, kbd, samp, .version-pill, .stage-stats, .community-params td, .cfg-input-number { font-variant-numeric: tabular-nums slashed-zero; }

.bb-skip {
  position: absolute; left: 16px; top: -80px; z-index: 100;
  padding: 10px 16px; border-radius: var(--cfg-radius-md);
  background: var(--cfg-color-surface-raised); color: var(--cfg-color-text);
  border: 1px solid var(--cfg-color-border-strong); font-size: var(--bb-fs-14); font-weight: 600; text-decoration: none;
}
.bb-skip:focus { top: 12px; }

/* -------------------------------- header --------------------------------- */
.bb-header {
  position: sticky; top: 0; z-index: 30;
  display: grid; grid-template-columns: auto 1fr auto; align-items: center; gap: 24px;
  padding: 12px max(20px, calc((100vw - var(--bb-max)) / 2 + 20px));
  background: color-mix(in srgb, var(--cfg-color-bg) 72%, transparent);
  -webkit-backdrop-filter: blur(16px) saturate(150%);
  backdrop-filter: blur(16px) saturate(150%);
  border-bottom: 1px solid var(--cfg-x-hairline);
}
/* The kit logo, inline: brackets and wordmark in Paper, studs in Stud Orange.
   The SVG carries a half-stud margin; the link adds the rest of the one-stud clear space. */
.bb-header-brand { display: inline-flex; align-items: center; min-height: 40px; padding: 0 2px; margin-left: -6px; text-decoration: none; color: inherit; border-radius: 8px; }
.bb-logo { display: block; flex: none; height: 36px; width: auto; overflow: visible; }
.bb-logo .bb-ink { fill: var(--cfg-color-text); }
.bb-logo .bb-stud { fill: var(--cfg-color-accent); }
.bb-logo-mark { display: none; }
.bb-glyph { display: block; flex: none; width: auto; height: 22px; }
.bb-glyph .bb-ink { fill: var(--cfg-color-text); }
.bb-glyph .bb-stud { fill: var(--cfg-color-accent); }

.bb-nav {
  position: relative; justify-self: start;
  display: flex; align-items: center; gap: 2px; padding: 4px;
  border: 1px solid var(--cfg-x-hairline); border-radius: var(--cfg-radius-pill);
  background: rgba(255,255,255,0.025);
  overflow-x: auto; scrollbar-width: none; max-width: 100%;
}
.bb-nav::-webkit-scrollbar { display: none; }
.bb-nav-ink, .bb-nav-hover { position: absolute; top: 4px; bottom: 4px; left: 0; border-radius: var(--cfg-radius-pill); pointer-events: none; }
/* active page: raised pill + orange tick */
.bb-nav-ink {
  width: var(--bb-ink-w, 0); transform: translateX(var(--bb-ink-x, 0)); opacity: var(--bb-ink-o, 0);
  background: var(--cfg-color-surface-raised);
  box-shadow: inset 0 0 0 1px var(--cfg-color-border-strong), 0 1px 0 rgba(255,255,255,0.04) inset;
}
.bb-nav-ink::after { content: ""; position: absolute; left: 50%; bottom: 3px; width: 14px; height: 2px; margin-left: -7px; border-radius: 2px; background: var(--cfg-color-accent); box-shadow: 0 0 6px var(--cfg-x-glow); }
.bb-nav.is-ready .bb-nav-ink { transition: transform var(--bb-dur-move) var(--bb-ease), width var(--bb-dur-move) var(--bb-ease), opacity var(--bb-dur-fade); }
/* hover/focus: a quiet pill, no tick */
.bb-nav-hover { width: 0; background: rgba(255,255,255,0.04); opacity: 0; transition: opacity var(--bb-dur-fade) var(--bb-ease-exit), transform var(--bb-dur-move) var(--bb-ease), width var(--bb-dur-move) var(--bb-ease); }
.bb-nav-hover.is-on { opacity: 1; transition-timing-function: var(--bb-ease); }
.bb-nav-hover.is-snap { transition: opacity var(--bb-dur-fade) var(--bb-ease); }
.bb-nav-link {
  position: relative; z-index: 1; flex: none;
  display: inline-flex; align-items: center; min-height: 36px; padding: 0 16px;
  border-radius: var(--cfg-radius-pill);
  font-size: var(--bb-fs-14); font-weight: 500; white-space: nowrap; text-decoration: none;
  color: var(--cfg-color-text-muted);
  transition: color var(--bb-dur-fade) var(--bb-ease);
}
.bb-nav-link.is-active, .bb-nav-link:focus-visible { color: var(--cfg-color-text); }
.bb-nav-link:focus-visible { outline-offset: -2px; border-radius: var(--cfg-radius-pill); }

.bb-account { display: flex; align-items: center; justify-content: flex-end; gap: 4px; }
.bb-account-btn {
  all: unset; box-sizing: border-box; cursor: pointer;
  display: inline-flex; align-items: center; gap: 10px; min-height: 40px; padding: 0 14px 0 6px;
  border: 1px solid var(--cfg-x-hairline); border-radius: var(--cfg-radius-pill);
  background: rgba(255,255,255,0.025);
  font-size: 13.5px; font-weight: 500; color: var(--cfg-color-text);
  transition: border-color var(--bb-dur-fade) var(--bb-ease), background var(--bb-dur-fade) var(--bb-ease);
}
.bb-account-btn.is-static { cursor: default; }
.bb-account-btn:focus-visible { outline: 2px solid var(--cfg-color-accent); outline-offset: 2px; }
.bb-account-btn.is-signin { padding: 0 18px; }
.bb-account-name { max-width: 14ch; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.bb-avatar { width: 28px; height: 28px; border-radius: 50%; display: inline-grid; place-items: center; flex: none; background: var(--cfg-color-surface-raised); box-shadow: inset 0 0 0 1px var(--cfg-color-border-strong); color: var(--cfg-color-text); font-size: var(--bb-fs-12); font-weight: 600; object-fit: cover; }
.bb-signout {
  all: unset; box-sizing: border-box; cursor: pointer;
  display: inline-flex; align-items: center; min-height: 40px; padding: 0 12px; border-radius: var(--cfg-radius-pill);
  font-size: var(--bb-fs-13); font-weight: 500; color: var(--cfg-color-text-muted);
  transition: color var(--bb-dur-fade) var(--bb-ease);
}
.bb-signout:focus-visible { outline: 2px solid var(--cfg-color-accent); outline-offset: 2px; color: var(--cfg-color-text); }

/* --------------------------------- page ---------------------------------- */
.page { max-width: var(--bb-max); padding: 40px 20px 24px; animation: bb-fade var(--bb-dur-fade) var(--bb-ease) both; }
.bb-first .page { animation: bb-rise var(--bb-dur-enter) var(--bb-ease) both; }
.page-head h1 { font-size: clamp(var(--bb-fs-28), 2.2vw + 14px, var(--bb-fs-40)); font-weight: 600; letter-spacing: -0.03em; line-height: 1.08; margin-bottom: 8px; }
.page-sub { font-size: 15.5px; line-height: 1.55; color: var(--cfg-color-text-muted); }
.page-catalog .page-head, .page-community .page-head { padding-bottom: 8px; }
.page-eyebrow {
  display: inline-flex; align-items: center; gap: 8px; margin: 0 0 14px;
  padding: 4px 10px; border-radius: var(--cfg-radius-pill);
  border: 1px solid var(--cfg-x-hairline); background: rgba(255,255,255,0.03);
  font-family: var(--cfg-font-body); font-size: var(--bb-fs-12); font-weight: 500; letter-spacing: 0.01em; text-transform: none;
  color: var(--cfg-color-text-muted);
}
.page-eyebrow::before { content: ""; width: 6px; height: 6px; border-radius: 1.5px; background: var(--cfg-color-border-strong); box-shadow: 0 0 0 1px rgba(255,255,255,0.06); }

@keyframes bb-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes bb-rise { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
@keyframes bb-shimmer { from { background-position: 150% 0; } to { background-position: -50% 0; } }
@keyframes bb-pulse { from { opacity: 0.35; } to { opacity: 1; } }
@keyframes bb-tick { from { border-color: rgba(255,90,31,0.6); color: var(--cfg-color-text); } }
@keyframes bb-done { 0% { transform: scale(1); } 40% { transform: scale(1.015); } 100% { transform: scale(1); } }

/* Loading states: shimmering text, plus a skeleton shaped like the card grid on list pages. */
p.muted.page, .page-catalog > p.muted:not(.small), .page-community > p.muted:not(.small) {
  background: linear-gradient(90deg, var(--cfg-color-text-muted) 0%, var(--cfg-color-text) 50%, var(--cfg-color-text-muted) 100%) 0 0 / 200% 100%;
  -webkit-background-clip: text; background-clip: text; color: transparent;
  animation: bb-shimmer var(--bb-dur-loop) linear infinite;
  width: fit-content;
}
p.muted.page { margin: 40px auto; padding-left: 20px; padding-right: 20px; min-height: 60vh; }
.page-catalog > p.muted:not(.small), .page-community > p.muted:not(.small) { min-height: 60vh; margin-top: 28px; }
.page-catalog > p.muted:not(.small)::after, .page-community > p.muted:not(.small)::after {
  --bb-cols: 4;
  content: ""; display: block; height: 420px; width: min(calc(var(--bb-max) - 40px), calc(100vw - 40px)); margin-top: 24px;
  background: linear-gradient(100deg, var(--cfg-color-surface) 30%, var(--cfg-color-surface-raised) 50%, var(--cfg-color-surface) 70%) 0 0 / 200% 100%;
  animation: bb-shimmer var(--bb-dur-loop) linear infinite;
  -webkit-mask-image: repeating-linear-gradient(90deg, #000 0 calc((100% - (var(--bb-cols) - 1) * 20px) / var(--bb-cols)), transparent 0 calc((100% - (var(--bb-cols) - 1) * 20px) / var(--bb-cols) + 20px));
  mask-image: repeating-linear-gradient(90deg, #000 0 calc((100% - (var(--bb-cols) - 1) * 20px) / var(--bb-cols)), transparent 0 calc((100% - (var(--bb-cols) - 1) * 20px) / var(--bb-cols) + 20px));
  border-radius: var(--cfg-radius-lg);
}
@media (max-width: 1260px) { .page-catalog > p.muted:not(.small)::after, .page-community > p.muted:not(.small)::after { --bb-cols: 3; } }
@media (max-width: 960px) { .page-catalog > p.muted:not(.small)::after, .page-community > p.muted:not(.small)::after { --bb-cols: 2; } }

/* --------------------------------- cards --------------------------------- */
.catalog-grid { grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 20px; margin-top: 28px; }
.catalog-grid > * { animation: bb-fade var(--bb-dur-fade) var(--bb-ease) both; }
/* first list of the session: a short rise, 30 ms steps, capped at four cards */
.bb-first .catalog-grid > * { animation: bb-rise var(--bb-dur-move) var(--bb-ease) both; }
.bb-first .catalog-grid > :nth-child(2) { animation-delay: var(--bb-stagger); }
.bb-first .catalog-grid > :nth-child(3) { animation-delay: calc(var(--bb-stagger) * 2); }
.bb-first .catalog-grid > :nth-child(n+4) { animation-delay: calc(var(--bb-stagger) * 3); }

.bb-card {
  position: relative; isolation: isolate;
  display: flex; flex-direction: column;
  text-decoration: none; color: inherit;
  background: linear-gradient(180deg, var(--cfg-color-surface-raised), var(--cfg-color-surface) 60%);
  border: 1px solid var(--cfg-color-border);
  border-radius: var(--cfg-radius-lg);
  box-shadow: inset 0 1px 0 var(--cfg-x-highlight);
  transition: transform var(--bb-dur-fade) var(--bb-ease), border-color var(--bb-dur-move) var(--bb-ease), box-shadow var(--bb-dur-move) var(--bb-ease);
}
/* pointer-following border glow and inner spotlight, positioned by transform from slots.tsx.
   The glow sits under a 1 px-inset cover painted like the card, so only the edge lights up. */
.bb-card-ring, .bb-card-fx { position: absolute; z-index: -1; border-radius: inherit; overflow: hidden; pointer-events: none; opacity: 0; transition: opacity var(--bb-dur-move) var(--bb-ease); }
.bb-card-ring { inset: -1px; }
.bb-card-ring::after { content: ""; position: absolute; inset: 1px; border-radius: calc(var(--cfg-radius-lg) - 1px); background: linear-gradient(180deg, var(--cfg-color-surface-raised), var(--cfg-color-surface) 60%); }
.bb-card-fx { inset: 0; }
.bb-card-spot, .bb-card-ring-spot { position: absolute; top: 0; left: 50%; border-radius: 50%; pointer-events: none; will-change: transform; }
.bb-card-spot { width: 840px; height: 840px; margin: -420px 0 0 -420px; background: radial-gradient(closest-side, rgba(255,255,255,0.055), transparent 55%); }
.bb-card-ring-spot { width: 520px; height: 520px; margin: -260px 0 0 -260px; background: radial-gradient(closest-side, rgba(255,122,71,0.9), rgba(255,90,31,0.25) 40%, transparent 70%); }
.bb-card:focus-visible { outline: 2px solid var(--cfg-color-accent); outline-offset: 3px; border-radius: var(--cfg-radius-lg); border-color: var(--cfg-color-border-strong); }
.bb-card:focus-visible .bb-card-fx, .bb-card:focus-visible .bb-card-ring { opacity: 1; }
.bb-card:focus-visible .bb-card-cta { color: var(--bb-accent-text); }
.bb-card:focus-visible .bb-card-preview img { transform: scale(1); }
/* touch press: 90 ms in, 160 ms out */
.bb-card:active { transform: scale(0.985); transition-duration: var(--bb-dur-press); }

.bb-card-preview {
  position: relative; aspect-ratio: 4 / 3; margin: 8px 8px 0; overflow: hidden;
  display: grid; place-items: center;
  border-radius: calc(var(--cfg-radius-lg) - 6px);
  border: 1px solid var(--cfg-x-hairline);
  background:
    radial-gradient(60% 55% at 50% 58%, rgba(255,90,31,0.07), transparent 70%),
    radial-gradient(circle, rgba(221,226,234,0.08) 1px, transparent 1.3px) 0 0 / 16px 16px,
    linear-gradient(180deg, #1F2A3B, #141C29);
}
.bb-card-preview::after { content: ""; position: absolute; left: 18%; right: 18%; bottom: 13%; height: 10%; border-radius: 50%; background: radial-gradient(closest-side, rgba(5,9,16,0.5), transparent); z-index: 0; }
.bb-card-preview img {
  position: relative; z-index: 1; width: 100%; height: 100%; object-fit: contain;
  filter: var(--bb-thumb-filter);
  transform: scale(0.94); transition: transform var(--bb-dur-enter) var(--bb-ease);
}
.bb-card-placeholder { font-family: var(--cfg-font-mono); font-size: var(--bb-fs-40); font-weight: 600; color: var(--cfg-color-border-strong); }

.bb-card-body { display: flex; flex-direction: column; gap: 8px; flex: 1; min-width: 0; padding: 16px 18px; }
.bb-card-head { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; }
.bb-card-title { margin: 0; font-size: var(--bb-fs-18); font-weight: 600; letter-spacing: -0.015em; line-height: 1.25; }
.bb-ver { flex: none; font-family: var(--cfg-font-mono); font-size: var(--bb-fs-11); color: var(--cfg-color-text-muted); font-variant-numeric: tabular-nums; }
.bb-card-desc { margin: 0; font-size: 13.5px; line-height: 1.55; color: var(--cfg-color-text-muted); display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
.bb-card-byline { -webkit-line-clamp: 2; }
.bb-card-foot { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: auto; padding-top: 6px; }
.bb-card-meta { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; min-width: 0; font-size: var(--bb-fs-12); color: var(--cfg-color-text-muted); }
.bb-tags { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.bb-signal { color: var(--cfg-color-success); }
.bb-card-cta { flex: none; display: inline-flex; align-items: center; gap: 4px; font-size: var(--bb-fs-13); font-weight: 600; color: var(--cfg-color-text-muted); transition: color var(--bb-dur-fade) var(--bb-ease); }
.bb-card-cta svg { transition: transform var(--bb-dur-move) var(--bb-ease); }

/* fit chips (neutral): set number in mono, name in body text */
.bb-card-fits { list-style: none; margin: 0; padding: 0; display: flex; flex-wrap: wrap; gap: 6px; min-width: 0; }
.bb-fit { display: inline-flex; align-items: baseline; gap: 6px; max-width: 100%; padding: 3px 8px; border-radius: var(--cfg-radius-sm); border: 1px solid var(--cfg-color-border); background: rgba(255,255,255,0.03); font-size: var(--bb-fs-12); line-height: 1.4; color: var(--cfg-color-text); }
.bb-fit-ref { font-family: var(--cfg-font-mono); font-size: var(--bb-fs-11); font-variant-numeric: tabular-nums; flex: none; }
.bb-fit-name { color: var(--cfg-color-text-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; }
.bb-fit-more { color: var(--cfg-color-text-muted); }

/* --------------------------------- empty --------------------------------- */
.bb-empty { display: grid; justify-items: center; gap: 4px; text-align: center; padding: 48px 24px; margin: 32px auto 8px; max-width: 560px; border: 1px dashed var(--cfg-color-border-strong); border-radius: var(--cfg-radius-lg); background: rgba(255,255,255,0.015); animation: bb-fade var(--bb-dur-fade) var(--bb-ease) both; }
.bb-empty .bb-glyph { height: 34px; margin-bottom: 10px; }
.bb-empty h2 { margin: 0; font-size: 22px; overflow-wrap: anywhere; }
.bb-empty p { margin: 4px 0 16px; color: var(--cfg-color-text-muted); max-width: 46ch; overflow-wrap: anywhere; }
.bb-empty > .cfg-btn { text-decoration: none; }

/* ---------------------------------- footer -------------------------------- */
.bb-footer { margin-top: 64px; border-top: 1px solid var(--cfg-x-hairline); background: rgba(10,15,23,0.35); }
.bb-footer-inner { max-width: var(--bb-max); margin: 0 auto; padding: 28px 20px 36px; display: grid; grid-template-columns: auto 1fr; gap: 6px 32px; font-family: var(--cfg-font-body); font-size: var(--bb-fs-13); color: var(--cfg-color-text-muted); }
.bb-footer-brand { grid-row: span 3; display: inline-flex; align-items: center; align-self: start; color: var(--cfg-color-text); text-decoration: none; border-radius: 6px; }
.bb-footer-brand .bb-logo { height: 26px; margin-left: -4px; }
.bb-footer-note { color: var(--cfg-color-text-muted); max-width: 72ch; }
.bb-footer-legal { font-size: var(--bb-fs-12); color: var(--cfg-color-text-muted); }

/* ------------------------------ configurator ------------------------------ */
.page-configurator { padding-top: 24px; }
.configurator-head { align-items: last baseline; margin-bottom: 20px; }
.configurator-head h1 { font-size: clamp(26px, 1.6vw + 14px, 34px); }
.configurator-head .page-sub { margin-bottom: 0; font-size: 14.5px; }
.configurator-meta { gap: 8px; }
.version-pill { font-family: var(--cfg-font-body); font-size: var(--bb-fs-12); padding: 3px 10px; border-color: var(--cfg-color-border-strong); background: rgba(255,255,255,0.03); color: var(--cfg-color-text-muted); }
.configurator-meta > .version-pill:first-child, .community-item-version { font-family: var(--cfg-font-mono); font-size: var(--bb-fs-11); }
.version-pill.is-warn { background: rgba(255,107,134,0.08); }
a.version-pill { text-decoration: none; }

.bb-frame { display: grid; grid-template-columns: minmax(0, 1fr); gap: 20px; }
.bb-frame-aside { font-size: var(--bb-fs-14); color: var(--cfg-color-text-muted); }
.print-notes { margin-top: 0; padding: 12px 16px; border: 1px solid var(--cfg-color-border); border-left: 3px solid var(--cfg-color-border-strong); border-radius: var(--cfg-radius-md); background: var(--cfg-color-surface); color: var(--cfg-color-text-muted); max-width: 80ch; }
.print-notes strong { color: var(--cfg-color-text); font-weight: 600; }

.configurator-layout { gap: 20px; }
.configurator-stage { top: calc(var(--bb-header-h) + 16px); gap: 10px; }
.configurator-stage .cfg-viewport, .configurator-stage .cfg-viewport-skeleton {
  height: clamp(420px, calc(100svh - var(--bb-header-h) - 196px), 900px);
  border: 1px solid var(--cfg-color-border);
  border-radius: var(--cfg-radius-lg);
  box-shadow: inset 0 1px 0 var(--cfg-x-highlight), var(--cfg-shadow-md);
}
.cfg-viewport-skeleton { background: radial-gradient(70% 60% at 50% 45%, #1F2A3B, #111925); }
.cfg-viewport { border-radius: var(--cfg-radius-lg); }
.cfg-viewport::before { content: ""; position: absolute; inset: 0; z-index: 1; pointer-events: none; border-radius: inherit; box-shadow: inset 0 0 120px rgba(5,9,16,0.22); }
/* "working": one 1.6 s rhythm, shown only after the shell's 250 ms busy delay */
.cfg-viewport.is-building::after {
  content: ""; position: absolute; inset: 0; z-index: 2; pointer-events: none;
  background: linear-gradient(100deg, transparent 35%, rgba(255,90,31,0.06) 50%, transparent 65%) 0 0 / 220% 100%;
  animation: bb-shimmer var(--bb-dur-loop) linear infinite, bb-fade var(--bb-dur-fade) var(--bb-ease) both;
}
.cfg-viewport-badge {
  top: 12px; right: 12px; z-index: 3; padding: 4px 10px 4px 22px;
  background: rgba(20,28,41,0.9);
  border-color: var(--cfg-color-border-strong); color: var(--cfg-color-text); font-size: var(--bb-fs-11);
  animation: bb-fade var(--bb-dur-fade) var(--bb-ease) both;
}
.cfg-viewport-badge::before { content: ""; position: absolute; left: 9px; top: 50%; width: 6px; height: 6px; margin-top: -3px; border-radius: 50%; background: var(--cfg-color-accent); box-shadow: 0 0 6px var(--cfg-color-accent); animation: bb-pulse calc(var(--bb-dur-loop) / 2) ease-in-out infinite alternate; }

.stage-stats { gap: 6px; font-family: var(--cfg-font-mono); font-size: var(--bb-fs-12); color: var(--cfg-color-text-muted); }
.stage-stats > span { padding: 4px 10px; border-radius: var(--cfg-radius-pill); border: 1px solid var(--cfg-x-hairline); background: rgba(255,255,255,0.025); white-space: nowrap; transition: border-color var(--bb-dur-fade), color var(--bb-dur-fade); }
.stage-stat-size { color: var(--cfg-color-text); border-color: var(--cfg-color-border-strong) !important; }
.stage-stat[data-changed] { animation: bb-tick 600ms var(--bb-ease-inout); }
.stage-stat-telemetry { display: none; }

/* floating dimension labels on the model: solid, no blur */
.cfg-float { padding: 4px 9px; border-radius: 8px; background: rgba(20,28,41,0.9); border-color: var(--cfg-color-border-strong); box-shadow: 0 4px 14px rgba(5,9,16,0.45); font-family: var(--cfg-font-mono); font-size: var(--bb-fs-12); transition: border-color var(--bb-dur-fade), color var(--bb-dur-fade), box-shadow var(--bb-dur-fade); }
.cfg-float-label { font-family: var(--cfg-font-body); font-size: 10.5px; letter-spacing: 0.01em; text-transform: none; }
.cfg-float-value { font-variant-numeric: tabular-nums; }
.cfg-float.is-active, .cfg-float.is-dragging { border-color: var(--cfg-color-accent); color: var(--bb-accent-text); box-shadow: 0 0 0 3px var(--cfg-color-accent-soft), 0 4px 14px rgba(5,9,16,0.45); }

/* the parameter panel: a lifted sheet; on tall desktops it scrolls on its own and keeps exports pinned */
.configurator-panel {
  --bb-pad: 18px;
  gap: 14px; padding: var(--bb-pad);
  background: var(--bb-panel);
  border: 1px solid var(--cfg-color-border);
  border-radius: var(--cfg-radius-lg);
  box-shadow: inset 0 1px 0 var(--cfg-x-highlight), var(--cfg-shadow-md);
}
.panel-title-row { align-items: center; }
.panel-title-row h2 { font-size: 17px; font-weight: 600; letter-spacing: -0.01em; }
.panel-title-row .cfg-chip:disabled { visibility: hidden; }
.panel-actions, .panel-save { gap: 10px; padding-top: 14px; border-top-color: var(--cfg-color-border); }
.panel-actions {
  position: sticky; bottom: calc(-1 * var(--bb-pad)); z-index: 2;
  display: grid; grid-template-columns: minmax(0, 1fr) auto; grid-auto-flow: row dense; column-gap: 12px; row-gap: 8px;
  margin: 0 calc(-1 * var(--bb-pad)); padding: 14px var(--bb-pad) var(--bb-pad);
  background: var(--bb-panel);
  box-shadow: 0 -12px 18px -14px rgba(5,9,16,0.75);
  border-bottom-left-radius: var(--cfg-radius-lg); border-bottom-right-radius: var(--cfg-radius-lg);
}
.panel-actions > .cfg-btn { grid-column: 1 / -1; }
.panel-actions .cfg-btn-primary { min-height: 46px; font-size: 15px; }
.cfg-actions-reason { grid-column: 1; display: flex; align-items: baseline; gap: 6px; margin: 0; font-size: var(--bb-fs-13); color: var(--cfg-color-danger); }
.cfg-actions-reason::before { content: "▲"; font-size: 10px; flex: none; }
.cfg-actions-reason button { text-decoration-color: color-mix(in srgb, currentColor 45%, transparent); text-underline-offset: 3px; }
.cfg-save-jump { grid-column: 2; justify-self: end; align-self: baseline; font-size: var(--bb-fs-13); color: var(--cfg-color-text-muted); text-decoration: none; padding: 2px 0; }
.cfg-live { grid-column: 1; margin: 0; min-height: 1.5em; font-size: var(--bb-fs-12); color: var(--cfg-color-text-muted); }

.panel-save { gap: 10px; }
.panel-save-title { margin: 0; font-family: var(--cfg-font-body); font-size: var(--bb-fs-12); font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: var(--cfg-color-text-muted); }
.panel-save-label { margin-bottom: -4px; }
.panel-save .muted.small, .panel-publish .muted.small { margin: 0; }
.panel-save-row { flex-wrap: wrap; }
.share-url a { font-family: var(--cfg-font-mono); font-size: var(--bb-fs-12); }
/* published: status first, then a quiet "Make private" */
.panel-publish .panel-save-row:has(> .version-pill) { align-items: center; justify-content: space-between; }
.panel-publish .panel-save-row > .version-pill { order: -1; align-self: center; display: inline-flex; align-items: center; gap: 6px; color: var(--cfg-color-success); border-color: rgba(67,211,148,0.35); background: rgba(67,211,148,0.08); }
.panel-publish .panel-save-row > .version-pill::before { content: ""; width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
.panel-publish .panel-save-row:has(> .version-pill) .cfg-publish-btn { flex: 0 0 auto; min-height: 36px; padding: 0 4px; background: none; border-color: transparent; box-shadow: none; color: var(--cfg-color-text-muted); font-weight: 500; font-size: var(--bb-fs-13); }

@media (min-width: 901px) {
  .configurator-layout { grid-template-columns: minmax(0, 1fr) 400px; }
}
/* Tall desktops: the panel is pinned and scrolls on its own, with a shadow when content is above. */
@media (min-width: 901px) and (min-height: 801px) {
  .configurator-panel {
    position: sticky; top: calc(var(--bb-header-h) + 16px);
    max-height: max(480px, calc(100svh - var(--bb-header-h) - 156px));
    overflow-y: auto; overscroll-behavior: contain;
    scrollbar-width: thin; scrollbar-gutter: stable;
    background:
      linear-gradient(var(--bb-panel) 40%, rgba(26,36,52,0)) center top / 100% 36px no-repeat local,
      radial-gradient(farthest-side at 50% 0, rgba(5,9,16,0.6), transparent) center top / 100% 14px no-repeat scroll,
      var(--bb-panel);
  }
}
/* Short desktops: the page scrolls, the export bar sticks to the window bottom. */
@media (min-width: 901px) and (max-height: 800px) {
  .panel-actions { bottom: 0; }
}

/* ---------------------------------- form ---------------------------------- */
.cfg-form { gap: 10px; }
.cfg-group { background: rgba(255,255,255,0.02); border-color: var(--cfg-color-border); border-radius: var(--cfg-radius-md); overflow: hidden; }
.cfg-group.has-error { border-color: rgba(255,107,134,0.45); }
.cfg-group-toggle {
  min-height: 44px; padding: 0 14px;
  font-family: var(--cfg-font-body); font-size: var(--bb-fs-12); font-weight: 600; letter-spacing: 0.08em;
  color: var(--cfg-color-text-muted);
  transition: color var(--bb-dur-fade) var(--bb-ease), background var(--bb-dur-fade) var(--bb-ease);
}
.cfg-group.is-open > .cfg-group-toggle { color: var(--cfg-color-text); }
.cfg-group-chevron { font-size: 0; width: 18px; height: 18px; display: grid; place-items: center; border-radius: 6px; }
.cfg-group-chevron::before {
  content: ""; width: 7px; height: 7px; margin-top: -3px;
  border-right: 1.6px solid var(--cfg-color-text-muted); border-bottom: 1.6px solid var(--cfg-color-text-muted);
  transform: rotate(45deg); transition: transform var(--bb-dur-move) var(--bb-ease-inout), border-color var(--bb-dur-fade);
}
.cfg-group.is-open .cfg-group-chevron::before { transform: translateY(3px) rotate(225deg); border-color: var(--cfg-color-text); }
.cfg-group-body { gap: 14px; padding: 14px; border-top-color: var(--cfg-color-border); }
@media (prefers-reduced-motion: no-preference) {
  .cfg-group-collapse { transition: grid-template-rows var(--bb-dur-move) var(--bb-ease-inout), visibility 0s linear 0s; }
  .cfg-group[data-state="closed"] .cfg-group-collapse { transition: grid-template-rows var(--bb-dur-move) var(--bb-ease-inout), visibility 0s linear var(--bb-dur-move); }
}

.cfg-field { gap: 6px; }
.cfg-label { font-family: var(--cfg-font-body); font-size: var(--bb-fs-13); font-weight: 500; letter-spacing: 0; text-transform: none; color: var(--cfg-color-text); align-items: baseline; gap: 8px; }
.cfg-unit { opacity: 1; font-family: var(--cfg-font-mono); font-size: var(--bb-fs-11); font-weight: 400; color: var(--cfg-color-text-muted); }
.cfg-help { font-size: 12.5px; line-height: 1.5; }
.cfg-error { font-size: 12.5px; }
.cfg-clamp-hint { font-size: var(--bb-fs-12); color: var(--bb-warn); }
.cfg-switch .cfg-label { font-weight: 500; }

/* print-check issue attached to a field */
.cfg-field-wrap { border-radius: var(--cfg-radius-sm); transition: background var(--bb-dur-fade) var(--bb-ease), box-shadow var(--bb-dur-fade) var(--bb-ease); }
.cfg-field-wrap.is-invalid { margin: -6px -8px; padding: 6px 8px; background: rgba(255,107,134,0.06); box-shadow: inset 3px 0 0 var(--cfg-color-danger); }
.cfg-field-issue { display: flex; gap: 6px; align-items: baseline; font-size: 12.5px; line-height: 1.45; }
.cfg-field-issue::before { content: "▲"; font-size: 10px; flex: none; }

.cfg-number-row { grid-template-columns: 1fr 6.5em; gap: 12px; }
/* slider: 4 px track, filled to --cfg-fill (set by configurator-ui), orange thumb */
.cfg-slider { -webkit-appearance: none; appearance: none; height: 24px; margin: 0; padding: 0; background: transparent; cursor: pointer; accent-color: var(--cfg-color-accent); border-radius: 4px; }
.cfg-slider::-webkit-slider-runnable-track { height: 4px; border-radius: 4px; background: linear-gradient(90deg, var(--cfg-color-accent) 0 var(--cfg-fill, 0%), var(--bb-track) var(--cfg-fill, 0%) 100%); }
.cfg-slider::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 16px; height: 16px; margin-top: -6px; border-radius: 50%; background: var(--cfg-color-accent); border: 2px solid var(--cfg-color-text); box-shadow: 0 1px 3px rgba(5,9,16,0.55); transition: box-shadow var(--bb-dur-fade) var(--bb-ease); }
.cfg-slider::-moz-range-track { height: 4px; border-radius: 4px; background: var(--bb-track); }
.cfg-slider::-moz-range-progress { height: 4px; border-radius: 4px; background: var(--cfg-color-accent); }
.cfg-slider::-moz-range-thumb { width: 12px; height: 12px; border-radius: 50%; background: var(--cfg-color-accent); border: 2px solid var(--cfg-color-text); box-shadow: 0 1px 3px rgba(5,9,16,0.55); }
.cfg-slider:focus { outline: none; }
.cfg-slider:focus-visible { outline: 2px solid var(--cfg-color-accent); outline-offset: 4px; }
.cfg-slider:active::-webkit-slider-thumb, .cfg-field-wrap.is-dragging .cfg-slider::-webkit-slider-thumb { box-shadow: 0 0 0 6px var(--cfg-color-accent-soft), 0 1px 3px rgba(5,9,16,0.55); }
.cfg-slider:disabled { opacity: 0.45; cursor: not-allowed; }

.cfg-input {
  min-height: 36px; padding: 6px 10px;
  font-family: var(--cfg-font-body); font-weight: 400; font-size: var(--bb-fs-14);
  background: var(--bb-field); color: var(--cfg-color-text);
  border: 1px solid var(--bb-input-border); border-radius: var(--cfg-radius-sm);
  transition: border-color var(--bb-dur-fade) var(--bb-ease), box-shadow var(--bb-dur-fade) var(--bb-ease);
}
.cfg-input-number, .cfg-fit-ref { font-family: var(--cfg-font-mono); font-weight: 500; font-size: var(--bb-fs-13); }
/* 2 px solid accent focus: 1 px border + 1 px ring */
.cfg-input:focus { outline: none; border-color: var(--cfg-color-accent); box-shadow: 0 0 0 1px var(--cfg-color-accent); }
.cfg-input.is-invalid, .cfg-input[aria-invalid="true"] { border-color: var(--cfg-color-danger); }
.cfg-input.is-invalid:focus, .cfg-input[aria-invalid="true"]:focus { box-shadow: 0 0 0 1px var(--cfg-color-danger), 0 0 0 4px rgba(255,107,134,0.2); }
.cfg-input::placeholder { color: #8E97A8; }
.cfg-input:disabled { opacity: 0.5; cursor: not-allowed; }
.cfg-input-number { text-align: right; font-variant-numeric: tabular-nums slashed-zero; }
.cfg-input-suffix { font-size: var(--bb-fs-11); color: var(--cfg-color-text-muted); }
select.cfg-input { padding-right: 28px; }
.cfg-textarea { line-height: 1.5; }
input[type="checkbox"]:not([role="switch"]) { accent-color: var(--cfg-color-accent); width: 16px; height: 16px; }
input[type="checkbox"]:not([role="switch"]):focus-visible { outline: 2px solid var(--cfg-color-accent); outline-offset: 2px; }

.cfg-switch { min-height: 32px; }
.cfg-switch-track { width: 36px; height: 20px; background: #6B768A; transition: background var(--bb-dur-fade) var(--bb-ease), box-shadow var(--bb-dur-fade); }
.cfg-switch-track::after { top: 3px; left: 3px; background: var(--cfg-color-text); box-shadow: 0 1px 2px rgba(5,9,16,0.45); transition: transform var(--bb-dur-move) var(--bb-ease); }
.cfg-switch input:checked + .cfg-switch-track { box-shadow: 0 0 10px rgba(255,90,31,0.3); }
.cfg-switch input:checked + .cfg-switch-track::after { background: #FFFFFF; }
.cfg-switch input:focus-visible + .cfg-switch-track { outline: 2px solid var(--cfg-color-accent); outline-offset: 2px; box-shadow: none; }

.cfg-segmented { display: flex; flex-wrap: wrap; gap: 2px; padding: 3px; border-color: var(--bb-input-border); border-radius: var(--cfg-radius-sm); background: var(--bb-field); }
.cfg-segment { flex: 1 1 auto; text-align: center; min-height: 30px; display: inline-flex; align-items: center; justify-content: center; padding: 0 12px; border: 0; border-radius: 4px; font-family: var(--cfg-font-body); font-size: var(--bb-fs-13); font-weight: 500; transition: background var(--bb-dur-fade) var(--bb-ease), color var(--bb-dur-fade) var(--bb-ease); }
.cfg-segment.is-active { background: var(--cfg-color-surface-raised); color: var(--cfg-color-text); box-shadow: inset 0 0 0 1px var(--cfg-color-border-strong), inset 0 -2px 0 var(--cfg-color-accent); font-weight: 600; }
.cfg-segment:focus-visible { outline: 2px solid var(--cfg-color-accent); outline-offset: 0; }

.cfg-presets { gap: 6px; }
.cfg-presets-label { font-family: var(--cfg-font-body); font-size: var(--bb-fs-12); font-weight: 600; letter-spacing: 0.08em; margin-right: 4px; }
.cfg-chip {
  display: inline-flex; align-items: center; min-height: 30px; padding: 0 12px;
  border: 1px solid var(--cfg-color-border-strong); border-radius: var(--cfg-radius-pill);
  font-family: var(--cfg-font-body); font-size: var(--bb-fs-13); font-weight: 500; color: var(--cfg-color-text);
  background: rgba(255,255,255,0.025);
  transition: border-color var(--bb-dur-fade) var(--bb-ease), color var(--bb-dur-fade) var(--bb-ease), background var(--bb-dur-fade) var(--bb-ease), transform var(--bb-dur-press) var(--bb-ease);
}
.cfg-chip:disabled { opacity: 0.45; cursor: not-allowed; }
.cfg-chip:focus-visible { outline: 2px solid var(--cfg-color-accent); outline-offset: 2px; }
.cfg-chip.is-active { border-color: var(--cfg-color-accent); color: var(--cfg-color-text); background: var(--cfg-color-accent-soft); }
.cfg-chip:active:not(:disabled) { transform: scale(0.97); }

/* fit tags: neutral chips; the set number is the only mono bit */
.cfg-fit-chip { padding: 4px 6px 4px 10px; background: rgba(255,255,255,0.035); border-color: var(--cfg-color-border-strong); font-family: var(--cfg-font-body); font-size: var(--bb-fs-12); }
.cfg-fit-chip a { color: var(--cfg-color-text); text-decoration: none; }
.cfg-fit-remove { display: inline-grid; place-items: center; width: 22px; height: 22px; padding: 0; border-radius: 50%; }
.cfg-fit-remove:focus-visible { outline: 2px solid var(--cfg-color-accent); outline-offset: 1px; }
.cfg-fit-row { gap: 6px; }
.cfg-fit-ref { flex: 0 0 9.5rem; }
.cfg-fit-name { flex: 1 1 8rem; }
.cfg-check { gap: 10px; min-height: 32px; font-size: 13.5px; }

/* print check: Stud Yellow rail for warnings, red for errors, grey for info; level glyphs */
.cfg-issues { background: rgba(255,184,28,0.05); border-color: var(--cfg-color-border); border-left: 3px solid var(--bb-warn); padding: 10px 14px; animation: bb-fade var(--bb-dur-fade) var(--bb-ease) both; }
.cfg-issues[data-level="error"] { border-left-color: var(--cfg-color-danger); background: rgba(255,107,134,0.06); }
.cfg-issues[data-level="info"] { border-left-color: var(--cfg-color-border-strong); background: rgba(255,255,255,0.02); }
.cfg-issues-title { font-family: var(--cfg-font-body); font-size: var(--bb-fs-12); font-weight: 600; letter-spacing: 0.08em; }
.cfg-issues ul { list-style: none; padding: 0; display: grid; gap: 4px; }
.cfg-issue { display: grid; grid-template-columns: 14px minmax(0, 1fr); gap: 6px; align-items: baseline; line-height: 1.5; font-size: var(--bb-fs-13); }
.cfg-issue::before { font-size: var(--bb-fs-13); line-height: 1; text-align: center; }
.cfg-issue[data-level="error"]::before { content: "▲"; font-size: 10px; color: var(--cfg-color-danger); }
.cfg-issue[data-level="warning"]::before { content: "●"; font-size: 10px; color: var(--bb-warn); }
.cfg-issue[data-level="info"]::before { content: "i"; font-family: var(--cfg-font-mono); font-weight: 600; color: var(--cfg-color-text-muted); }
.cfg-issue.is-warning { color: var(--cfg-color-text); }

/* --------------------------------- buttons -------------------------------- */
.cfg-btn {
  min-height: 40px; padding: 0 18px;
  font-family: var(--cfg-font-body); font-size: var(--bb-fs-14); font-weight: 600; letter-spacing: -0.005em;
  background: var(--cfg-color-surface-raised); color: var(--cfg-color-text);
  border: 1px solid var(--cfg-color-border-strong); border-radius: var(--cfg-radius-md);
  box-shadow: inset 0 1px 0 var(--cfg-x-highlight);
  transition: transform var(--bb-dur-fade) var(--bb-ease), box-shadow var(--bb-dur-fade) var(--bb-ease), background var(--bb-dur-fade) var(--bb-ease), border-color var(--bb-dur-fade) var(--bb-ease), color var(--bb-dur-fade), filter var(--bb-dur-fade) var(--bb-ease);
}
.cfg-btn:active:not(:disabled) { transform: translateY(1px) scale(0.985); transition-duration: var(--bb-dur-press); }
.cfg-btn-primary {
  background: linear-gradient(180deg, #FF6A33, var(--cfg-color-accent));
  border-color: #FF7A47; color: var(--cfg-color-accent-contrast);
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.28), 0 1px 2px rgba(5,9,16,0.45);
}
/* momentarily unavailable (another export or a save is running): keep the look, dim only after
   the busy delay so quick operations never flicker */
.cfg-btn:disabled { opacity: 0.55; cursor: not-allowed; transition: opacity var(--bb-dur-fade) var(--bb-ease) var(--bb-delay-busy); }
/* blocked by print issues: hollow, dashed, clearly not clickable (STL and 3MF alike) */
.panel-actions[data-blocked] > .cfg-btn:disabled {
  background: transparent; border: 1px dashed var(--cfg-color-border-strong); color: var(--bb-disabled-text);
  box-shadow: none; opacity: 1; filter: none; transition: none;
}
/* working on it (preparing / exporting / saving): the button stays put and dims after the busy delay */
.cfg-btn.is-busy, .cfg-btn[data-phase] { cursor: progress; opacity: 1; }
.cfg-btn.is-busy, .cfg-btn[data-phase] { filter: saturate(0.6) brightness(0.85); transition: filter var(--bb-dur-fade) var(--bb-ease) var(--bb-delay-busy); }
/* success: a brief green edge on the button that just worked */
.cfg-btn.is-done { border-color: var(--cfg-color-success); color: var(--cfg-color-success); animation: bb-done var(--bb-dur-enter) var(--bb-ease); }
.cfg-btn-primary.is-done { color: var(--cfg-color-accent-contrast); border-color: var(--cfg-color-success); box-shadow: inset 0 1px 0 rgba(255,255,255,0.28), 0 0 0 3px rgba(67,211,148,0.28); }

.cfg-text-btn { color: var(--cfg-color-text-muted); }
.cfg-text-btn:focus-visible { border-radius: 4px; }

/* -------------------------------- other pages ------------------------------ */
.community-filters { gap: 10px; margin-top: 20px; }
.community-field-label { font-size: var(--bb-fs-12); font-weight: 500; color: var(--cfg-color-text-muted); }
.community-filters .cfg-input { min-height: 40px; }
.community-search-input { font-family: var(--cfg-font-body); }
.community-field-design { flex: 0 1 15rem; }
.community-clear { color: var(--cfg-color-text-muted); }
.community-popular { gap: 6px; margin-top: 14px; }
.community-matched { margin-top: 10px; }
.community-empty-actions { margin-top: 4px; }
.community-more { margin-top: 24px; text-align: center; }
.community-signals .version-pill { color: var(--cfg-color-success); border-color: rgba(67,211,148,0.35); background: rgba(67,211,148,0.08); }
.community-notes { padding: 14px 16px; border-radius: var(--cfg-radius-md); background: var(--cfg-color-surface); border: 1px solid var(--cfg-color-border); }

.page-community-item .page-head h1 { max-width: 24ch; }
.page-community-item .page-sub { font-size: var(--bb-fs-14); }
.community-item-design { font-weight: 500; }
.community-item-preview {
  border-color: var(--cfg-color-border);
  background: radial-gradient(70% 60% at 50% 45%, #1F2A3B, #111925);
  box-shadow: inset 0 1px 0 var(--cfg-x-highlight), var(--cfg-shadow-md);
}
@media (min-width: 900px) { .community-item-preview { top: calc(var(--bb-header-h) + 16px); } }
.community-item-thumb { filter: var(--bb-thumb-filter); padding: 6%; }
.community-item-body { max-width: 38rem; }
.community-params-title { font-size: var(--bb-fs-16); font-weight: 600; }
.community-params { width: 100%; max-width: 40rem; border: 1px solid var(--cfg-color-border); border-radius: var(--cfg-radius-md); border-collapse: separate; border-spacing: 0; overflow: hidden; background: var(--cfg-color-surface); font-family: var(--cfg-font-body); font-size: var(--bb-fs-13); }
.community-params:not(:has(tr)) { display: none; }
.community-params th, .community-params td { padding: 9px 14px; border-bottom-color: var(--cfg-color-border); }
.community-params tr:last-child th, .community-params tr:last-child td { border-bottom: 0; }
.community-params th { font-family: var(--cfg-font-body); color: var(--cfg-color-text-muted); width: 55%; }
.community-params td { color: var(--cfg-color-text); text-align: right; font-family: var(--cfg-font-mono); }
.community-params tr.is-default td { color: var(--cfg-color-text-muted); }
.community-param-unit { font-size: var(--bb-fs-11); }
.community-actions { gap: 8px; margin-top: 8px; }
.community-item-foot { border-top-color: var(--cfg-x-hairline); }
.community-legal { font-size: var(--bb-fs-12); }

.mine-filter-label { font-size: var(--bb-fs-12); font-weight: 500; }
.mine-filter-field { margin-bottom: 16px; }
.mine-tags { align-items: center; }
.mine-tags .cfg-fit-chip { padding: 2px 8px; }
.mine-profile { color: var(--cfg-color-text-muted); }
.mine-list { gap: 8px; }
.mine-item { padding: 10px 14px 10px 10px; background: var(--cfg-color-surface); border-color: var(--cfg-color-border); transition: border-color var(--bb-dur-fade) var(--bb-ease), background var(--bb-dur-fade) var(--bb-ease); }
.mine-item:focus-within { border-color: var(--cfg-color-border-strong); }
.mine-item-link { border-radius: var(--cfg-radius-md); }
.mine-thumb { border: 1px solid var(--cfg-x-hairline); background: radial-gradient(circle, rgba(221,226,234,0.07) 1px, transparent 1.3px) 0 0 / 10px 10px, linear-gradient(180deg, #1F2A3B, #141C29); }
.mine-thumb img { filter: drop-shadow(0 4px 6px rgba(5,9,16,0.5)); padding: 4px; }
.mine-item-name { color: var(--cfg-color-text); font-weight: 600; }
.mine-item-meta { margin-top: 2px; }
.mine-tags .version-pill { font-size: var(--bb-fs-11); }
.mine-item.is-confirming { border-color: rgba(255,107,134,0.5); background: rgba(255,107,134,0.04); }
.mine-delete-yes { border-color: rgba(255,107,134,0.5); color: var(--cfg-color-danger); }

/* ------------------------- hover (fine pointers only) ----------------------- */
@media (hover: hover) and (pointer: fine) {
  .bb-nav-link:hover { color: var(--cfg-color-text); }
  .bb-account-btn:not(.is-static):hover { border-color: var(--cfg-color-border-strong); background: rgba(255,255,255,0.05); }
  .bb-signout:hover { color: var(--cfg-color-text); }
  .bb-card:hover { transform: translateY(-3px); border-color: var(--cfg-color-border-strong); box-shadow: inset 0 1px 0 var(--cfg-x-highlight), var(--cfg-shadow-lg); transition-duration: var(--bb-dur-move); }
  .bb-card:hover .bb-card-fx, .bb-card:hover .bb-card-ring { opacity: 1; }
  .bb-card:hover .bb-card-preview img { transform: scale(1); }
  .bb-card:hover .bb-card-cta { color: var(--bb-accent-text); }
  .bb-card:hover .bb-card-cta svg { transform: translateX(3px); }
  .bb-card:active { transform: translateY(-1px); }
  a.version-pill:hover { border-color: var(--cfg-color-text-muted); color: var(--cfg-color-text); }
  .cfg-float:hover { border-color: var(--cfg-color-accent); color: var(--bb-accent-text); }
  .cfg-group-toggle:hover { color: var(--cfg-color-text); background: rgba(255,255,255,0.025); }
  .cfg-input:hover:not(:disabled):not(:focus) { border-color: var(--bb-input-border-hover); }
  .cfg-slider:hover::-webkit-slider-thumb { box-shadow: 0 0 0 5px var(--cfg-color-accent-soft), 0 1px 3px rgba(5,9,16,0.55); }
  .cfg-segment:hover:not(.is-active) { color: var(--cfg-color-text); background: rgba(255,255,255,0.04); }
  .cfg-chip:hover:not(:disabled) { border-style: solid; border-color: var(--cfg-color-text-muted); color: var(--cfg-color-text); background: rgba(255,255,255,0.06); }
  .cfg-fit-chip a:hover { color: var(--bb-accent-text); }
  .cfg-fit-remove:hover { background: rgba(255,107,134,0.15); }
  .cfg-btn:hover:not(:disabled) { background: #28344A; border-color: #4A5568; color: var(--cfg-color-text); }
  .cfg-btn-primary:hover:not(:disabled) {
    background: linear-gradient(180deg, #FF7A47, #FF6A33); border-color: #FF8C5E; color: var(--cfg-color-accent-contrast);
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.32), 0 6px 22px -4px var(--cfg-x-glow), 0 0 0 4px rgba(255,90,31,0.12);
  }
  .cfg-text-btn:hover, .cfg-save-jump:hover { color: var(--cfg-color-text); }
  .panel-publish .panel-save-row:has(> .version-pill) .cfg-publish-btn:hover:not(:disabled) { background: none; border-color: transparent; color: var(--cfg-color-text); text-decoration: underline; }
  .community-clear:hover:not(:disabled) { color: var(--cfg-color-text); }
  .mine-item:hover { border-color: var(--cfg-color-border-strong); background: var(--cfg-color-surface-raised); }
  button.link.community-report-link:hover, button.link.mine-delete:hover { color: var(--cfg-color-text); }
}

/* ------------------------------ narrow screens ----------------------------- */
@media (max-width: 900px) {
  .configurator-layout { grid-template-columns: minmax(0, 1fr); gap: 14px; }
  /* Keep the model in view while editing: the stage pins to the top, the panel scrolls under it. */
  .app-shell:has(.page-configurator) .bb-header { position: relative; }
  .configurator-stage {
    --bb-stage-h: clamp(220px, 40svh, 460px);
    top: 0; z-index: 5; margin: 0 -16px; padding: 8px 16px 10px;
    background: var(--cfg-color-bg);
    border-bottom: 1px solid var(--cfg-x-hairline);
    box-shadow: 0 10px 20px -12px rgba(5,9,16,0.75);
  }
  .configurator-stage .cfg-viewport, .configurator-stage .cfg-viewport-skeleton { height: var(--bb-stage-h); min-height: 0; }
  /* typing: give the form the room, the model stays in sight as a strip */
  .page-configurator:has(.configurator-panel .cfg-input:focus) .configurator-stage { --bb-stage-h: 22svh; }
  .page-configurator:has(.configurator-panel .cfg-input:focus) .panel-actions { position: static; box-shadow: none; }
  /* revealed fields land below the pinned stage, not behind it */
  .cfg-field-wrap, .cfg-issues, .panel-save { scroll-margin-top: calc(40svh + 96px); }
  .stage-stats { flex-wrap: nowrap; overflow-x: auto; scrollbar-width: none; }
  .stage-stats::-webkit-scrollbar { display: none; }
  .configurator-panel { --bb-pad: 14px; }
  /* compact export bar: two buttons side by side, reason under them, result line floats above */
  .panel-actions { grid-template-columns: 1fr 1fr; row-gap: 6px; column-gap: 8px; bottom: 0; margin-bottom: calc(-1 * var(--bb-pad)); padding: 10px var(--bb-pad); box-shadow: 0 -12px 24px -12px rgba(5,9,16,0.65); border-radius: 0; }
  .panel-actions > .cfg-btn { grid-column: auto; min-width: 0; width: auto; }
  .panel-actions .cfg-btn-primary { min-height: 44px; font-size: var(--bb-fs-14); }
  .panel-actions > .cfg-btn:only-of-type { grid-column: 1 / -1; }
  .cfg-actions-reason { grid-column: 1 / -1; font-size: 12.5px; }
  .cfg-save-jump { display: none; }
  .panel-actions > .cfg-live { position: absolute; left: var(--bb-pad); right: var(--bb-pad); bottom: calc(100% + 6px); min-height: 0; margin: 0; pointer-events: none; }
  .panel-actions > .cfg-live:not(:empty) { padding: 6px 10px; border-radius: var(--cfg-radius-sm); background: var(--cfg-color-surface-raised); border: 1px solid var(--cfg-color-border-strong); color: var(--cfg-color-text); box-shadow: var(--cfg-shadow-md); animation: bb-fade var(--bb-dur-fade) var(--bb-ease) both; }
}

/* Landscape phones: no pinned strip; model and form side by side, model sticks. */
@media (max-height: 500px) and (min-width: 600px) {
  .app-shell:has(.page-configurator) .bb-header { position: relative; }
  .configurator-layout { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 14px; }
  .configurator-stage { --bb-stage-h: calc(100svh - 64px); position: sticky; top: 8px; margin: 0; padding: 0; border: 0; box-shadow: none; background: none; }
  .page-configurator:has(.configurator-panel .cfg-input:focus) .configurator-stage { --bb-stage-h: calc(100svh - 64px); }
  .cfg-field-wrap, .cfg-issues, .panel-save { scroll-margin-top: 12px; }
}
@media (max-height: 500px) and (max-width: 599px) {
  .configurator-stage { position: static; }
  .cfg-field-wrap, .cfg-issues, .panel-save { scroll-margin-top: 12px; }
}

@media (max-width: 720px) {
  .bb-header { grid-template-columns: 1fr auto; grid-template-areas: "brand account" "nav nav"; gap: 8px 12px; padding: 8px 16px 10px; }
  .bb-header-brand { grid-area: brand; min-height: 44px; }
  .bb-header-brand .bb-logo { height: 30px; }
  .bb-account { grid-area: account; }
  .bb-nav { grid-area: nav; justify-self: stretch; border-radius: 12px; }
  .bb-nav-ink, .bb-nav-hover { border-radius: 9px; }
  .bb-nav-link { min-height: 40px; padding: 0 14px; border-radius: 9px; flex: 1 0 auto; justify-content: center; }
  .bb-account-btn { min-height: 44px; padding: 0 8px; }
  .bb-signout { min-height: 44px; }
  .bb-account-name { display: none; }
  .page { padding: 24px 16px 16px; }
  .page-configurator { padding-top: 16px; }
  .page-head h1 { font-size: var(--bb-fs-28); }
  .page-sub { font-size: 14.5px; }
  .configurator-head { margin-bottom: 14px; }
  .configurator-head h1 { font-size: var(--bb-fs-24); }
  /* cards go horizontal: thumbnail left, text right */
  .catalog-grid { grid-template-columns: minmax(0, 1fr); gap: 12px; margin-top: 20px; }
  .bb-card { flex-direction: row; align-items: stretch; }
  .bb-card-preview { flex: 0 0 36%; aspect-ratio: 1; margin: 8px 0 8px 8px; align-self: center; }
  .bb-card-body { padding: 12px 14px; gap: 6px; }
  .bb-card-title { font-size: var(--bb-fs-16); }
  .bb-card-desc { -webkit-line-clamp: 2; font-size: var(--bb-fs-13); }
  .bb-card-cta { display: none; }
  .bb-fit-name { display: none; }
  .bb-footer-inner { grid-template-columns: 1fr; padding: 24px 16px 32px; }
  .bb-footer-brand { grid-row: auto; margin-bottom: 6px; }
  /* community search: field + button on one row, design filter under it */
  .community-filters { display: grid; grid-template-columns: minmax(0, 1fr) auto; grid-template-areas: "search actions" "design design"; align-items: end; }
  .community-field-search { grid-area: search; }
  .community-field-design { grid-area: design; }
  .community-filter-actions { grid-area: actions; }
  .community-filter-actions .cfg-btn { padding: 0 14px; }
  .community-params th { width: auto; }
  .community-actions .cfg-btn { flex: 1 1 100%; }
  .cfg-btn { white-space: nowrap; }
  .cfg-fit-ref, .cfg-fit-name { flex: 1 1 100%; }
  .page-catalog > p.muted:not(.small)::after, .page-community > p.muted:not(.small)::after { --bb-cols: 1; width: calc(100vw - 32px); height: 300px; }
}

/* Very narrow screens: the [••] mark alone keeps its clear space next to the account controls. */
@media (max-width: 359px) {
  .bb-header-brand .bb-logo { display: none; }
  .bb-header-brand .bb-logo-mark { display: block; }
}

/* Touch: every control at least 44 px tall; inline links get a taller hit area. */
@media (pointer: coarse) {
  .cfg-btn, .cfg-input, .cfg-group-toggle, .bb-nav-link, .bb-account-btn, .bb-signout { min-height: 44px; }
  .cfg-chip, .cfg-segment { min-height: 44px; }
  .cfg-segment { padding: 0 14px; }
  .cfg-switch, .cfg-check { min-height: 44px; }
  .cfg-slider { height: 44px; }
  .cfg-slider::-webkit-slider-thumb { width: 22px; height: 22px; margin-top: -9px; }
  .cfg-fit-remove { width: 36px; height: 36px; }
  .cfg-input { font-size: 16px; } /* no iOS zoom on focus */
  button.link, .cfg-text-btn, .cfg-issue-link, .cfg-actions-reason button, .community-item-design { padding-block: 10px; margin-block: -10px; }
  .page-sub a, .community-fits a { padding-block: 10px; }
}

/* Reduced motion: nothing moves; colour and opacity still fade, briefly. */
@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after {
    animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; animation-delay: 0ms !important;
    transition-property: opacity, color, background-color, border-color, box-shadow, filter !important;
    transition-duration: 120ms !important; transition-delay: 0ms !important;
    scroll-behavior: auto !important;
  }
  p.muted.page, .page-catalog > p.muted:not(.small), .page-community > p.muted:not(.small) { animation: none !important; background: none; color: var(--cfg-color-text-muted); }
  .page-catalog > p.muted:not(.small)::after, .page-community > p.muted:not(.small)::after { animation: none !important; }
  .cfg-viewport.is-building::after { animation: none !important; background: rgba(255,90,31,0.03); }
  .bb-card:hover, .bb-card:active, .bb-card:focus-visible, .cfg-btn:active:not(:disabled), .cfg-chip:active:not(:disabled) { transform: none; }
  .bb-card:hover .bb-card-preview img, .bb-card:focus-visible .bb-card-preview img { transform: scale(0.94); }
  .bb-card:hover .bb-card-cta svg { transform: none; }
  .bb-bench-lens { display: none; }
}
`;
